import { AspectRatio, Box, Flex, Icon, Image, Tooltip } from "@highoutput/ui-components";
import { gql } from "graphql-request";
import Link from "next/link";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeVideo from "rehype-video";
import rehypeWidont from "rehype-widont";
import remarkGfm from "remark-gfm";
import client from "~/config/client";
import LightBulbIcon from "../icons/lightbulb";

export default function MainContent({ markdown }: { markdown: string }) {
  const [content, setContent] = useState("");
  const [preview, setPreview] = useState<{
    [key: string]: {
      title: string;
      image: string;
    };
  }>({});

  useEffect(() => {
    setContent(markdown ?? "");

    return () => setContent("");
  }, [markdown]);

  return (
    <ReactMarkdown
      /* @ts-expect-error "Ref: https://github.com/orgs/rehypejs/discussions/63" */
      rehypePlugins={[rehypeRaw, rehypeWidont, [rehypeVideo, { details: false }]]}
      remarkPlugins={[remarkGfm]}
      components={{
        caption({ children, ...props }) {
          return (
            <span className="caption" {...props}>
              {children}
            </span>
          );
        },
        img({ node: _, ...props }) {
          const credit = getImageCredit(props.alt);

          if (!credit) return <Image alt="" {...props} />;

          // we are adding credits to image here
          // credits are setup on the alt prop 😂
          return (
            <Box
              as="span"
              display="block"
              marginBottom="calc(58px - 16px)" /* This should be the same to image style */
            >
              <Image
                alt=""
                sx={{
                  marginBottom: "0px !important",
                }}
                {...props}
              />

              <Box
                as="span"
                sx={{
                  display: "block",
                  marginTop: {
                    base: "12px",
                    lg: "16px",
                  },
                  fontSize: "14px",
                  lineHeight: {
                    base: "14px",
                    lg: "20px",
                  },
                  letterSpacing: {
                    base: "0.02em",
                    lg: "unset",
                  },
                  fontFamily: "'Inter', sans-serif",
                  color: "rgba(13,48,80,0.6)",
                }}
              >
                Image courtesy of {credit.author} via{" "}
                <a href={credit.url} target="_blank" rel="noreferrer">
                  {credit.source}
                </a>
              </Box>
            </Box>
          );
        },
        /*
         * We need to display the title whenever a link is hovered
         * if anchor's href is an article from advante
         *
         */
        a({ children, href, ...props }) {
          let slug = "";

          if (!href) {
            return (
              <a href={href} target="_blank" rel="noreferrer" {...props}>
                {children}
              </a>
            );
          }

          if (ADVANTE_ARTICLE_PATTERN.test(href)) {
            slug = href.replace(ADVANTE_ARTICLE_PATTERN, "");

            if (!preview[slug]) {
              getPostTitleAndCoverBySlug(slug).then((o) => {
                setPreview((old) => ({
                  ...old,
                  [slug]: {
                    title: o?.title ?? "",
                    image: o?.cover.data.attributes.url ?? "",
                  },
                }));
              });
            }
          }

          if (!preview[slug]) {
            return (
              <a href={href} target="_blank" rel="noreferrer" {...props}>
                {children}
              </a>
            );
          }

          return (
            <Tooltip
              as="span"
              placement="top"
              hasArrow
              display="block"
              rounded="8px"
              bgColor="white"
              color="rgba(13,48,80,0.8)"
              fontSize="12px"
              lineHeight="18px"
              fontFamily="'Inter', sans-serif"
              fontWeight="medium"
              padding="8px"
              maxWidth="375px"
              maxHeight="unset"
              border="1px solid"
              borderColor="rgba(240,237,236,0.2)"
              boxShadow={
                "0px 12px 16px -4px rgba(16, 24, 40, 0.08)," +
                "0px 4px 6px -2px rgba(16, 24, 40, 0.03)"
              }
              label={
                <Box as="span" display="block">
                  <AspectRatio
                    ratio={16 / 9}
                    maxHeight="200px"
                    marginBottom="2"
                    rounded="4px"
                    overflow="hidden"
                  >
                    <Image src={preview[slug].image} alt="" />
                  </AspectRatio>
                  {preview[slug].title}
                </Box>
              }
              shouldWrapChildren
            >
              <Box as={Link} href={href}>
                {children}
              </Box>
            </Tooltip>
          );
        },
        /* need to insert a 💡 icon to blockquotes */
        blockquote({ children }) {
          return (
            <Box as="blockquote">
              <Flex
                width="56px"
                height="56px"
                alignItems="center"
                justifyContent="center"
                bgColor="v2.primary.default"
                rounded="full"
                marginBottom="32px"
              >
                <Icon
                  as={LightBulbIcon}
                  stroke="#0D3050
"
                  width="28px"
                  height="28px"
                />
              </Flex>
              {children}
            </Box>
          );
        },
        div({ children, ...props }) {
          return (
            <span className="block" {...props}>
              {children}
            </span>
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

function getImageCredit(alt?: string) {
  if (!alt?.trim().length) return null;

  // alt format is "source|url|author"
  // eg. "unsplash|https://unsplash.com/blaa%7Cjohn doe"
  const array = alt.split(/\|/g);

  if (array.length < 3) return null;

  const [source, url, author] = array;

  return {
    url,
    source,
    author,
  };
}

async function getPostTitleAndCoverBySlug(slug: string) {
  const doc = gql`
    query Posts($slug: String!) {
      posts(pagination: { limit: 1 }, publicationState: LIVE, filters: { slug: { eq: $slug } }) {
        data {
          attributes {
            title
            cover {
              data {
                attributes {
                  url
                }
              }
            }
          }
        }
      }
    }
  `;

  try {
    const { posts } = await client.request<
      {
        posts: {
          data: {
            attributes: {
              title: string;
              cover: {
                data: {
                  attributes: {
                    url: string;
                  };
                };
              };
            };
          }[];
        };
      },
      {
        slug: string;
      }
    >(doc, { slug });

    return posts?.data?.at(0)?.attributes;
  } catch {
    return null;
  }
}

const ADVANTE_ARTICLE_PATTERN = /^https:\/\/advante.co\/blog\//;
